import { useEffect, useId, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
} from '@mui/material';
import { Box, Stack, Typography } from '../components/mui-system';
import { api } from '../api/client';
import type { AlertDetail, AlertMail } from '../../../api/src/alerts/alerts.types';

// Keep stored evidence intact. Escapes expose invisible controls and remain valid JSON,
// including supplementary Unicode characters represented as UTF-16 surrogate pairs.
function visibleControls(value: string): string {
  return value.replace(/[\p{Cc}\p{Cf}\p{Zl}\p{Zp}]/gu, (character) =>
    ['\n', '\r', '\t'].includes(character)
      ? character
      : character
          .split('')
          .map((unit) => `\\u${unit.charCodeAt(0).toString(16).padStart(4, '0')}`)
          .join(''),
  );
}

export function AlertsSource({
  evidence,
  source,
}: {
  evidence: AlertMail | null;
  source: 'mail' | 'api';
}) {
  const [tab, setTab] = useState(0);
  const id = useId();
  if (!evidence) {
    return (
      <Alert severity="info">
        Исходные данные недоступны. Срок хранения исходных текстов — 90 дней; решение и состояние
        доставки сохраняются.
      </Alert>
    );
  }
  return (
    <Stack gap={2} sx={{ minWidth: 0 }}>
      <Typography variant="body2" color="text.secondary">
        {source === 'mail'
          ? 'Входные данные до AI-анализа. Это не полный оригинал email.'
          : 'Сохранённые данные события API: свидетельство, сформированное дашбордом, а не полный ответ Яндекса.'}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Разметка и ссылки не активны. Скрытые управляющие символы — <code>\uXXXX</code>.
      </Typography>
      <Tabs
        value={tab}
        onChange={(_, value: number) => setTab(value)}
        aria-label="Формат исходных данных"
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
      >
        <Tab id={`${id}-text`} aria-controls={`${id}-panel`} label="Текст" />
        <Tab id={`${id}-json`} aria-controls={`${id}-panel`} label="Входные данные JSON" />
      </Tabs>
      <Box
        role="tabpanel"
        id={`${id}-panel`}
        aria-labelledby={`${id}-${tab === 0 ? 'text' : 'json'}`}
      >
        <Box
          component="pre"
          tabIndex={0}
          aria-label={tab === 0 ? 'Исходный текст' : 'JSON входных данных'}
          sx={{
            m: 0,
            p: 2,
            maxHeight: '50vh',
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
            fontSize: 13,
            bgcolor: 'action.hover',
            direction: 'ltr',
            unicodeBidi: 'plaintext',
          }}
        >
          {visibleControls(tab === 0 ? evidence.text : JSON.stringify(evidence, null, 2))}
        </Box>
      </Box>
    </Stack>
  );
}

export function AlertsSourceDialog({
  workspace,
  eventId,
  onClose,
}: {
  workspace: string;
  eventId: string;
  onClose(): void;
}) {
  const [attempt, setAttempt] = useState(0);
  const [response, setResponse] = useState<{
    workspace: string;
    eventId: string;
    detail: AlertDetail | null;
    error: string;
  } | null>(null);
  const current =
    response?.workspace === workspace && response.eventId === eventId ? response : null;
  const detail = current?.detail;
  const error = current?.error;
  const titleId = useId();
  useEffect(() => {
    let active = true;
    void api.alert(workspace, eventId).then(
      (result) => {
        if (active) setResponse({ workspace, eventId, detail: result, error: '' });
      },
      () => {
        if (active)
          setResponse({
            workspace,
            eventId,
            detail: null,
            error: 'Не удалось загрузить исходные данные. Попробуйте ещё раз.',
          });
      },
    );
    return () => {
      active = false;
    };
  }, [workspace, eventId, attempt]);
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth aria-labelledby={titleId}>
      <DialogTitle id={titleId}>Исходник события</DialogTitle>
      <DialogContent>
        {error ? (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                onClick={() => {
                  setResponse(null);
                  setAttempt((value) => value + 1);
                }}
              >
                Повторить
              </Button>
            }
          >
            {error}
          </Alert>
        ) : detail ? (
          <AlertsSource
            key={`${workspace}:${eventId}`}
            evidence={detail.evidence}
            source={detail.source}
          />
        ) : (
          <CircularProgress aria-label="Загрузка исходных данных" />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Закрыть</Button>
      </DialogActions>
    </Dialog>
  );
}
